/**
 * My Trainings — the staff-facing side of TRUS-486.
 *
 * Every Amigos employee gets a login, so this reaches all ~100 of them: what they owe,
 * what they have completed, and — where the training allows it — a way to report a
 * completion themselves.
 *
 * Self-reporting appears only for trainings whose `record_update_permission` is
 * `staff_self_report`. The backend enforces that too; showing the control for anything
 * else would just produce a 403 the person cannot act on.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { GraduationCap, Info, Plus } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader, StatTile, StatTileGrid } from '@/components/weavein'
import { Button, ErrorNote, Field, INPUT, Modal, Pill, TableShell, Td, Th } from './ui'
import {
  Assignment,
  TrainingRecord,
  createRecords,
  formatDate,
  isDoneUntilNextDue,
  myAssignments,
  myRecords,
  statusTone,
} from './api'

const STATUS_LABEL: Record<Assignment['status'], string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  pending: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const today = () => new Date().toISOString().slice(0, 10)

export default function MyTrainingsPage() {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [records, setRecords] = useState<TrainingRecord[]>([])
  const [selfReportable, setSelfReportable] = useState<{ id: number; title: string; hours: number | null }[]>([])
  const [userId, setUserId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [reportOpen, setReportOpen] = useState(false)
  const [trainingId, setTrainingId] = useState('')
  const [trainingDate, setTrainingDate] = useState(today())
  // TRUS-549 ask 4b. Not a dialog gate: the server requires it on a self-report and
  // stores it, because record retention for this program began 1/1/2026.
  const [attested, setAttested] = useState(false)
  // A platform admin (super_admin / ops_admin) may ACT in this org but holds no
  // user_organizations row in it, so a completion cannot be recorded against
  // them here — the server refuses it, correctly, because the record would land
  // in this org's compliance roster as though they were staff. Before this the
  // page offered them the control anyway and the attempt came back as
  // "user(s) not in this organization: [5]".
  const [isOrgMember, setIsOrgMember] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [a, r] = await Promise.all([myAssignments(), myRecords()])
      setAssignments(a.assignments)
      setRecords(r.records)
      setUserId(r.user_id)
      setSelfReportable(r.self_reportable_trainings.map((t) => ({ id: t.id, title: t.title, hours: t.hours })))
      // Defaults TRUE when the field is absent, so an older API (or a cached
      // deploy) keeps the previous behaviour rather than hiding the control
      // from everyone.
      setIsOrgMember(r.is_org_member !== false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your training record.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const submit = async () => {
    if (!trainingId || !userId || !attested) return
    setSaving(true)
    setActionError(null)
    try {
      await createRecords({
        training_id: Number(trainingId),
        training_date: trainingDate,
        user_ids: [userId],
        source: 'manual',
        // TRUS-766: a report of your own training is self-reported, even for an admin
        // (Jose's were being saved as admin-entered, without the attestation).
        reported_by: 'self',
        attested: true,
      })
      setReportOpen(false)
      setTrainingId('')
      setTrainingDate(today())
      setAttested(false)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not record your completion.')
    } finally {
      setSaving(false)
    }
  }

  // TRUS-562: a repeating training done for this cycle is not outstanding; its next due
  // date shows beside the latest completion instead.
  const outstanding = assignments.filter((a) => !isDoneUntilNextDue(a))
  const nextDue = new Map<number, string>()
  for (const a of assignments) if (isDoneUntilNextDue(a) && a.due_date) nextDue.set(a.training_id, a.due_date)
  const latestRecordIds = new Set<number>()
  const seenTrainings = new Set<number>()
  for (const r of records) {
    // Records arrive newest first, so the first one per training is the latest.
    if (!seenTrainings.has(r.training_id)) {
      seenTrainings.add(r.training_id)
      latestRecordIds.add(r.id)
    }
  }

  const overdue = assignments.filter((a) => a.status === 'overdue').length
  const dueSoon = assignments.filter((a) => a.status === 'due_soon').length
  const totalHours = records.reduce((sum, r) => sum + (r.hours ?? 0), 0)

  return (
    <>
      <PageHeader
        eyebrow="Training · My record"
        title="My trainings"
        description="What you owe, and what you have completed."
        actions={
          isOrgMember && selfReportable.length > 0 ? (
            <Button variant="primary" onClick={() => setReportOpen(true)}>
              <Plus className="h-4 w-4" /> Report a completion
            </Button>
          ) : undefined
        }
      />
      <PageContainer>
        <div className="space-y-4">
          {actionError && <ErrorNote message={actionError} onDismiss={() => setActionError(null)} />}

          {loading ? (
            <PageState variant="loading" title="Loading your record" />
          ) : error ? (
            <PageState
              variant="error"
              title="Could not load your record"
              message={error}
              action={<Button onClick={() => void load()}>Retry</Button>}
            />
          ) : (
            <>
              {!isOrgMember && (
                // Says why the Report a completion button is absent, instead of
                // letting an admin hunt for a control that was removed. The
                // record would land in THIS org's compliance roster, so the
                // refusal is about whose numbers these are, not permissions —
                // and an admin reading "you are not a member" needs that said.
                <div className="flex gap-2 rounded-[10px] border border-v2border bg-paper-2 px-4 py-3 text-[13px] text-ink-500">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-400" />
                  <span>
                    You can administer this organization, but you are not on its staff, so training
                    completions cannot be recorded against you here — they would count towards this
                    organization&apos;s compliance. To record one for somebody who does work here, use{' '}
                    <strong>Training &rsaquo; Records</strong>.
                  </span>
                </div>
              )}

              <StatTileGrid>
                <StatTile label="Overdue" value={overdue} tone={overdue > 0 ? 'danger' : 'success'} />
                <StatTile label="Due soon" value={dueSoon} tone={dueSoon > 0 ? 'attention' : 'neutral'} />
                <StatTile label="Completed" value={records.length} tone="neutral" />
                <StatTile
                  label="Training hours"
                  value={Number.isInteger(totalHours) ? totalHours : totalHours.toFixed(2)}
                  tone="neutral"
                />
              </StatTileGrid>

              <SectionHeader title="Outstanding" />
              {outstanding.length === 0 ? (
                <PageState
                  variant="empty"
                  icon={<GraduationCap className="h-5 w-5 text-ink-400" />}
                  title="Nothing outstanding"
                  message="You have no training due right now."
                />
              ) : (
                <TableShell>
                  <thead>
                    <tr>
                      <Th>Training</Th>
                      <Th>Status</Th>
                      <Th>Due</Th>
                      <Th>Hours</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstanding.map((a) => (
                      <tr key={a.id} className="hover:bg-paper-2">
                        <Td className="font-medium">
                          {a.training_title}
                          {a.is_mandatory === true && (
                            <span className="ml-2">
                              <Pill tone="attention">Mandatory</Pill>
                            </span>
                          )}
                          {a.signup_url && (
                            <a
                              href={a.signup_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-[12px] font-normal text-v2accent hover:underline"
                            >
                              Take it
                            </a>
                          )}
                        </Td>
                        <Td>
                          <Pill tone={statusTone(a.status)}>{STATUS_LABEL[a.status]}</Pill>
                        </Td>
                        <Td className="whitespace-nowrap">
                          {a.due_date ? formatDate(a.due_date) : <span className="text-ink-400">Not scheduled</span>}
                        </Td>
                        <Td className="tabular-nums">{a.hours === null ? '—' : a.hours}</Td>
                      </tr>
                    ))}
                  </tbody>
                </TableShell>
              )}

              <SectionHeader title="Completed" />
              {records.length === 0 ? (
                <PageState variant="empty" title="No completions recorded yet" />
              ) : (
                <TableShell>
                  <thead>
                    <tr>
                      <Th>Training</Th>
                      <Th>Date</Th>
                      <Th>Hours</Th>
                      <Th>Recorded by</Th>
                      <Th>Certificate</Th>
                      <Th>Next due</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className="hover:bg-paper-2">
                        <Td className="font-medium">{r.training_title}</Td>
                        <Td className="whitespace-nowrap">{formatDate(r.training_date)}</Td>
                        <Td className="tabular-nums">{r.hours === null ? '—' : r.hours}</Td>
                        <Td>{r.reported_by === 'self' ? 'You' : 'An administrator'}</Td>
                        <Td className="text-ink-500">{r.certificate ? r.certificate.filename : '—'}</Td>
                        <Td className="whitespace-nowrap text-ink-500">
                          {latestRecordIds.has(r.id) && nextDue.has(r.training_id)
                            ? formatDate(nextDue.get(r.training_id)!)
                            : '—'}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </TableShell>
              )}
            </>
          )}
        </div>
      </PageContainer>

      <Modal
        open={reportOpen}
        title="Report a completion"
        description="Only trainings your organization lets staff self-report appear here."
        onClose={() => {
          setReportOpen(false)
          setAttested(false)
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setReportOpen(false)
                setAttested(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!trainingId || !attested || saving}
            >
              {saving ? 'Recording…' : 'Record it'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Training" required>
            <select className={INPUT} value={trainingId} onChange={(e) => setTrainingId(e.target.value)}>
              <option value="">Choose a training</option>
              {selfReportable.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date you completed it" required hint="Cannot be in the future.">
            <input
              className={INPUT}
              type="date"
              max={today()}
              value={trainingDate}
              onChange={(e) => setTrainingDate(e.target.value)}
            />
          </Field>
          <label className="flex cursor-pointer items-start gap-2 rounded-[10px] border border-v2border bg-paper px-3 py-2.5 text-[13px] text-ink-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
            />
            <span>By marking this training as completed, you are certifying that you have completed the training and provided accurate details.</span>
          </label>
        </div>
      </Modal>
    </>
  )
}
